import { css } from 'styled-components';

import type { CSSProperties } from 'react';

/**
 * Left gutter reserved inside the transcript's scroll container so the topic
 * rail has somewhere to paint. `ScrollableText` sets `overflow-y: scroll`,
 * which computes `overflow-x` to `auto` — anything drawn left of the content
 * box is clipped, so the space has to be real rather than a negative offset
 * into the surrounding padding.
 *
 * Reserved for the whole time a surface participates in topic filtering (see
 * `$topicGutter` on each view's `Wrapper`) — NOT only while something is
 * matched. Reserving it on demand slid the transcript text sideways every time
 * a topic was selected or cleared (PLAT-11039). Views that never receive topic
 * props keep their original indent.
 */
export const TOPIC_RAIL_GUTTER_PX = 6;

/**
 * Constant left padding on each view's inner `Wrapper`, so the playhead cursor
 * has room when it lands at the start of a line. The text therefore sits this
 * far right of its wrapper's box — which is why the header adds it too (see
 * `transcriptHeaderGutterCss`) to end up on the same edge.
 */
export const CURSOR_ALLOWANCE_PX = 2;

/**
 * Story-topic match indicator: a vertical rail in the left gutter rather than
 * an underline on the matched text, which competed with the word-level
 * highlights and read as noise across a multi-line match.
 *
 * Apply to a block-level paragraph element carrying `.topic-highlighted` /
 * `.topic-active` — set imperatively by `adjustDOMClassesToTopicHighlight`
 * (TranscriptionText) or rendered via `topicRailProps` (translated views) —
 * plus an optional `--topic-highlight-color` inline style for the per-topic
 * color.
 */
export const topicRailCss = css`
  position: relative;

  &.topic-highlighted::before,
  &.topic-active::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    /* Hard against the scroll container's padding-box edge — gutter plus the
       wrapper's cursor allowance left of the paragraph box. Any further left
       would clip (the scroller computes overflow-x: auto). */
    left: -${TOPIC_RAIL_GUTTER_PX + CURSOR_ALLOWANCE_PX}px;
    width: 2px;
    border-radius: 2px;
    background-color: var(--topic-highlight-color, var(--badge-blue-text));
    opacity: 0.4;
  }

  /* The prev/next counter's current target reads as the solid rail; the other
     matches stay muted so the active one is findable at a glance. */
  &.topic-active::before {
    width: 3px;
    opacity: 1;
  }
`;

/**
 * className + CSS-var style for a paragraph in the translated views, which
 * render topic state through React instead of the imperative DOM pass.
 */
export function topicRailProps(
  paragraphId: string | undefined,
  highlightedParagraphIds: ReadonlySet<string> | undefined,
  activeParagraphId: string | undefined,
  paragraphHighlightColors: ReadonlyMap<string, string> | undefined,
): { className?: string; style?: CSSProperties } {
  if (!paragraphId) return {};
  const classes: string[] = [];
  if (highlightedParagraphIds?.has(paragraphId)) {
    classes.push('topic-highlighted');
  }
  if (paragraphId === activeParagraphId) classes.push('topic-active');
  if (classes.length === 0) return {};
  const color = paragraphHighlightColors?.get(paragraphId);
  return {
    className: classes.join(' '),
    style: color
      ? ({ '--topic-highlight-color': color } as CSSProperties)
      : undefined,
  };
}

/**
 * True when this surface takes part in topic filtering, so the rail gutter has
 * to be reserved. Keyed on the *presence* of `highlightedParagraphIds` rather
 * than its size: hosts hand down an empty set while topics are live but
 * nothing is matched, which keeps the gutter — and therefore the text's
 * horizontal position — stable across selecting, clearing and mode switches
 * (PLAT-11039).
 */
export function reservesTopicGutter(
  highlightedParagraphIds: ReadonlySet<string> | undefined,
  activeParagraphId: string | undefined,
): boolean {
  return highlightedParagraphIds !== undefined || !!activeParagraphId;
}

/**
 * Rail gutter for the **scroll container** (`ScrollableText`) — real space the
 * rail can paint into, since `overflow-y: scroll` computes `overflow-x: auto`
 * and clips at the padding box.
 *
 * It used to live on each view's inner `Wrapper`, which sits inside the scroll
 * container while the header is a sibling above it — so the transcript text
 * was always a gutter-width right of the toolbar (PLAT-11039). Pairing this
 * with `transcriptHeaderGutterCss` puts both on one edge.
 *
 * No transition: the gutter no longer changes within a surface, and animating
 * it was what made the old on-demand reservation read as the text sliding.
 */
export const topicGutterCss = css<{ $topicGutter?: boolean }>`
  padding-left: ${props => (props.$topicGutter ? TOPIC_RAIL_GUTTER_PX : 0)}px;
`;

/**
 * The first header control (the search PanelButton) insets its glyph this far
 * inside its own box — the hover-pill padding. The header compensates for it
 * so the GLYPH, not the button box, sits on the shared edge (the hover pill
 * bleeds slightly left instead, exactly like the language trigger).
 */
const HEADER_GLYPH_INSET_PX = 4;

/**
 * The header's matching inset: the scroll container's gutter plus the cursor
 * allowance the text carries inside its wrapper, minus the first button's
 * glyph inset. Keeps the search icon and the first character of transcript
 * text on the same vertical line — `--spacing-sm` from the pane edge, i.e.
 * flush with the content tab strip's labels.
 */
export const transcriptHeaderGutterCss = css<{ $topicGutter?: boolean }>`
  padding-left: ${props =>
    Math.max(
      (props.$topicGutter ? TOPIC_RAIL_GUTTER_PX : 0) +
        CURSOR_ALLOWANCE_PX -
        HEADER_GLYPH_INSET_PX,
      0,
    )}px;
`;
